import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import EditTeamMember from '../Team/EditTeamMember';
import Attendance from '../Team/Attendance';
import '../Team/team.css';
import './salaries.css';

// Management → Salaries (manager-only). Monthly payroll that replaces the Excel
// sheet. Spec: PLAN_salaries.md.
//
// Money rule: MOHRE is fixed; holiday/bonus/deductions/absence all move the
// CONDITIONAL side. Total = MOHRE + adjusted conditional (server is the source
// of truth — every edit round-trips and re-reads the recomputed figures).
// Advances are pre-payments (paid, not a deduction). Holiday/Bonus are itemised
// additions; deductions are itemised too — both support a daily-rate multiplier.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Totals are always rounded UP to whole AED (no stray .53).
const moneyWhole = (n) => `AED ${Math.ceil(Number(n || 0)).toLocaleString('en-US')}`;
// Bare rounded-up number (no "AED") for the compact table buttons.
const whole = (n) => Math.ceil(Number(n || 0)).toLocaleString('en-US');
const fullName = (e) => [e.first_name, e.last_name].filter(Boolean).join(' ');
// yyyy-mm-dd (already local-safe from the server) → dd/mm/yyyy for display.
const fmtDate = (s) => (s ? s.split('-').reverse().join('/') : '—');
// For timestamps (paid_at, item created_at) — fmtDate only handles YYYY-MM-DD.
const fmtStamp = (s) => {
    if (!s) return '—';
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};
// Trim trailing zeros so 3.00 → 3 and 1.20 → 1.2 (day multipliers especially).
const clean = (x) => (x === '' || x == null || isNaN(Number(x)) ? '' : String(Number(x)));
const dayLabel = (a) => `${clean(a)}× daily`;

// One editable numeric cell. Commits on blur only when the value changed.
// `step` controls arrow granularity (1 for whole-day fields).
function EditCell({ value, onCommit, prefix, width = 74, disabled, step = '0.01' }) {
    const [v, setV] = useState(clean(value));
    useEffect(() => { setV(clean(value)); }, [value]);
    return (
        <span className="sal-cellWrap">
            {prefix && <span className="sal-cellPrefix">{prefix}</span>}
            <input className="sal-cell" type="number" step={step} value={v} disabled={disabled}
                style={{ width }}
                onChange={(e) => setV(e.target.value)}
                onFocus={(e) => e.target.select()}
                onBlur={() => { if (Number(v || 0) !== Number(value || 0)) onCommit(v === '' ? 0 : Number(v)); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
        </span>
    );
}

// Annual-leave cell: compact — the used-days editor + the balance. The labour-card
// date that drives it lives in the per-member edit panel (keeps this column slim).
function LeaveCell({ e }) {
    const lv = e.leave;
    if (!lv) return <span className="sal__leaveNa" title="Annual leave applies to company-ID staff only">—</span>;
    // Read-only: annual leave used is driven ENTIRELY by the Attendance tab.
    return (
        <div className="sal__leaveWrap">
            <span className="sal__leaveUsed" title="Used this month — mark annual leave in the Attendance tab">{num(e.annual_leave_days_used)} used</span>
            <span className="sal__leaveBal" title={`${lv.service_years} full year${lv.service_years === 1 ? '' : 's'} from ${fmtDate(lv.start_date)} → ${lv.accrued} days (30/year, carried over) − ${lv.used_total} taken to end of this month`}>{lv.balance} left</span>
            {/* Leave already marked in LATER months — not spent yet, so it sits
                beside the balance rather than inside it. Without it, a person
                whose next month is fully booked still reads as having days free. */}
            {num(lv.booked_ahead) > 0 && (
                <span className="sal__leaveAheadChip" title={`${lv.booked_ahead} day${num(lv.booked_ahead) === 1 ? '' : 's'} already booked in later months → ${lv.remaining} still bookable`}>+{lv.booked_ahead} booked</span>
            )}
        </div>
    );
}

export default function Salaries() {
    // Managers see both tabs; supervisors/admins only the Attendance tab (no pay).
    const isManager = String(localStorage.getItem('userRole') || '').toLowerCase() === 'manager';
    // Team-page button deep-links here with ?tab=attendance for easy navigation.
    const wantsAttendance = new URLSearchParams(window.location.search).get('tab') === 'attendance';
    const [activeTab, setActiveTab] = useState(!isManager || wantsAttendance ? 'attendance' : 'salaries');
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [dedDrawer, setDedDrawer] = useState(null); // { entryId, name }
    const [bonusDrawer, setBonusDrawer] = useState(null); // { entryId, name }
    const [bulk, setBulk] = useState(null); // { mode: 'deduct' | 'bonus' }
    const [teamEdit, setTeamEdit] = useState(null); // { entryId } — salary & dates panel
    const [selected, setSelected] = useState(() => new Set());
    const [search, setSearch] = useState('');
    const [summaryId, setSummaryId] = useState(null); // entry id for the summary modal
    const [sifBusy, setSifBusy] = useState(false);
    const [sifResult, setSifResult] = useState(null); // { filename, summary, file_base64, downloaded }
    const sifInputRef = useRef(null);
    const sifFileRef = useRef(null); // last uploaded template, for manual re-fill

    // Minimal action feedback: a thin progress bar at the top whenever ANY
    // salaries API call is in flight. An axios interceptor counts them, so
    // every button/drawer action is covered without per-handler wiring.
    const [pendingOps, setPendingOps] = useState(0);
    useEffect(() => {
        const isSal = (cfg) => typeof cfg?.url === 'string' && cfg.url.startsWith('/api/salaries');
        const req = axios.interceptors.request.use((cfg) => { if (isSal(cfg)) setPendingOps((n) => n + 1); return cfg; });
        const res = axios.interceptors.response.use(
            (r) => { if (isSal(r?.config)) setPendingOps((n) => Math.max(0, n - 1)); return r; },
            (e) => { if (isSal(e?.config)) setPendingOps((n) => Math.max(0, n - 1)); return Promise.reject(e); }
        );
        return () => { axios.interceptors.request.eject(req); axios.interceptors.response.eject(res); };
    }, []);

    // Clicking a row opens the summary — but not when the click was on an input,
    // button, label or the name (those have their own actions).
    const openSummary = (ev, e) => {
        if (ev.target.closest('input, button, a, label, .sal-cell')) return;
        setSummaryId(e.id);
    };

    const fetchMonth = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/salaries/${year}/${month}`);
            setData(res.data);
        } catch (err) {
            toast.error('Failed to load salaries');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => { if (isManager) fetchMonth(); }, [fetchMonth, isManager]);

    // Switch tab, keep the URL in sync (so a reload lands on the same tab), and
    // refresh the salary figures on the way back — attendance edits (e.g. annual
    // leave) flow into payroll, so the table must re-read them.
    const switchTab = (tab) => {
        setActiveTab(tab);
        const url = new URL(window.location);
        if (tab === 'attendance') url.searchParams.set('tab', 'attendance');
        else url.searchParams.delete('tab');
        window.history.replaceState({}, '', url);
        if (tab === 'salaries' && isManager) fetchMonth();
    };
    useEffect(() => { setSelected(new Set()); }, [year, month]); // clear selection on month change

    const step = (delta) => {
        let m = month + delta, y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y);
    };

    const openMonth = async () => {
        setBusy(true);
        try {
            const res = await axios.post(`/api/salaries/${year}/${month}/open`);
            setData(res.data);
            toast.success(`${MONTHS[month - 1]} ${year} opened`);
        } catch (err) {
            toast.error('Failed to open month');
        } finally { setBusy(false); }
    };

    const saveField = async (id, field, value) => {
        try { setData((await axios.put(`/api/salaries/entries/${id}`, { [field]: value })).data); }
        catch (err) { toast.error('Save failed'); fetchMonth(); }
    };
    const pay = async (id, body) => {
        try { setData((await axios.post(`/api/salaries/entries/${id}/pay`, body)).data); }
        catch (err) { toast.error('Payment update failed'); }
    };

    // Line-item mutations — all update `data` so an open drawer (reading the live
    // entry out of `data`) re-renders immediately.
    const addDeduction = async (entryId, body) => { setData((await axios.post(`/api/salaries/entries/${entryId}/deductions`, body)).data); };
    const removeDeduction = async (id) => { setData((await axios.delete(`/api/salaries/deductions/${id}`)).data); };
    const addAddition = async (entryId, body) => { setData((await axios.post(`/api/salaries/entries/${entryId}/additions`, body)).data); };
    const removeAddition = async (id) => { setData((await axios.delete(`/api/salaries/additions/${id}`)).data); };
    // Edit, so correcting a mistyped amount does not mean deleting the line and
    // retyping it from scratch (2026-08-31). Same shape as add/remove: the PUT
    // returns the whole reloaded run, so an open drawer re-renders off `data`.
    const editDeduction = async (id, body) => { setData((await axios.put(`/api/salaries/deductions/${id}`, body)).data); };
    const editAddition = async (id, body) => { setData((await axios.put(`/api/salaries/additions/${id}`, body)).data); };

    const applyBulk = async (mode, body) => {
        const entry_ids = selected.size ? [...selected] : undefined;
        const url = mode === 'deduct' ? `/api/salaries/${year}/${month}/bulk-deduction` : `/api/salaries/${year}/${month}/bulk-addition`;
        const res = await axios.post(url, { ...body, entry_ids });
        setData(res.data);
        return res.data;
    };
    const bulkDelete = async (mode, ids) => {
        const seg = mode === 'deduct' ? 'deductions' : 'additions';
        const res = await axios.post(`/api/salaries/${year}/${month}/${seg}/bulk-delete`, { ids });
        setData(res.data);
        return res.data;
    };

    const removeStaff = async (id, name) => {
        if (!window.confirm(`Remove ${name} from ${MONTHS[month - 1]} ${year}’s payroll?\n\nTheir data is kept and you can add them back from below the table. Other months are unaffected.`)) return;
        if (!window.confirm(`Are you sure? ${name} will be taken off this month’s payroll and totals.`)) return;
        try { setData((await axios.delete(`/api/salaries/entries/${id}`)).data); toast.success('Removed from this month'); }
        catch (err) { toast.error('Failed to remove'); }
    };
    const restoreStaff = async (id) => {
        try { setData((await axios.post(`/api/salaries/entries/${id}/restore`)).data); toast.success('Added back to this month'); }
        catch (err) { toast.error('Failed to add back'); }
    };

    // SIF-oriented CSV of the source figures the accountant needs for the WPS SIF.
    const exportCsv = () => {
        if (!data?.entries?.length) return;
        const head = ['Name', 'Role', 'MOHRE (basic)', 'Daily rate', 'Conditional (variable)', 'Additions', 'Deductions', 'Advance', 'Paid Days', 'Leave Used', 'Net Total', 'Paid', 'Remaining'];
        const rows = data.entries.map((e) => [
            fullName(e), e.role_snapshot, e.mohre_salary, e.computed.daily_rate, e.computed.conditional_effective,
            e.computed.additions_total, e.computed.total_deductions, e.computed.advance,
            e.paid_days, e.annual_leave_days_used, e.computed.total, e.computed.paid_total, e.computed.remaining,
        ]);
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csv = [head, ...rows].map((r) => r.map(esc).join(',')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url; a.download = `salaries-${year}-${String(month).padStart(2, '0')}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    };

    // Upload the month's Al Ansari WPS SIF template → backend fills the monthly
    // figures into the matching rows (by MOL number). If some file rows aren't
    // in our payroll, the download is HELD and the modal asks for their MOHRE +
    // variance so the manager can fill them manually (or download without them).
    const downloadSifFile = (file_base64, filename) => {
        const bytes = Uint8Array.from(atob(file_base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.ms-excel' }));
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    };
    const uploadSif = async (file, manualRows = null) => {
        if (!file) return;
        sifFileRef.current = file; // keep it so manual figures can re-fill the same file
        setSifBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            if (manualRows?.length) fd.append('manual_rows', JSON.stringify(manualRows));
            const res = await axios.post(`/api/salaries/${year}/${month}/sif-fill`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const { file_base64, filename, summary } = res.data;
            const missing = summary.not_in_payroll?.length || 0;
            if (missing > 0 && !manualRows) {
                // Hold the download — let the manager type the missing figures first.
                setSifResult({ filename, summary, file_base64, downloaded: false });
                toast.info(`${missing} in the SIF ${missing === 1 ? 'is' : 'are'}n’t in payroll — enter their figures or download without them`);
            } else {
                downloadSifFile(file_base64, filename);
                setSifResult({ filename, summary, file_base64, downloaded: true });
                const flags = missing + (summary.in_payroll_not_in_file?.length || 0);
                toast.success(`SIF filled: ${summary.filled_count} employee${summary.filled_count === 1 ? '' : 's'}${flags ? ` · ${flags} to review` : ''}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to fill SIF');
        } finally {
            setSifBusy(false);
            if (sifInputRef.current) sifInputRef.current.value = '';
        }
    };
    const downloadSifAsIs = () => {
        if (!sifResult) return;
        downloadSifFile(sifResult.file_base64, sifResult.filename);
        setSifResult((r) => ({ ...r, downloaded: true }));
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (data?.entries || []).filter((e) => !q || fullName(e).toLowerCase().includes(q));
    }, [data, search]);

    const groups = useMemo(() => {
        const g = [];
        const byRole = {};
        filtered.forEach((e) => {
            const role = e.role_snapshot || 'other';
            if (!byRole[role]) { byRole[role] = []; g.push([role, byRole[role]]); }
            byRole[role].push(e);
        });
        return g;
    }, [filtered]);

    const allIds = filtered.map((e) => e.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
    const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const selCount = selected.size;
    const targetLabel = selCount ? `${selCount} selected` : `all ${allIds.length}`;

    // WhatsApp the payslip PDF to each selected staff member. The server
    // renders each slip (same builder as Generate Payslip), parks it on
    // Spaces and sends the hhc_salary_slip document template one by one.
    // Sending is IRREVERSIBLE, so the button only opens a review dialog that
    // names every recipient and their number — the actual send needs a second,
    // deliberate click there (the owner: the one-click version was too easy to hit).
    const [sendingSlips, setSendingSlips] = useState(false);
    const [slipReview, setSlipReview] = useState(false);
    const sendPayslips = async () => {
        const n = selected.size;
        if (!n || sendingSlips) return;
        setSlipReview(false);
        setSendingSlips(true);
        try {
            const r = await axios.post(`/api/salaries/${year}/${month}/payslips/send`, { entry_ids: [...selected] });
            const { sent = 0, failed = 0, results = [] } = r.data || {};
            if (failed) {
                const names = results.filter((x) => !x.ok).map((x) => `${x.name} — ${x.error}`).join('; ');
                toast.warn(`Sent ${sent}, failed ${failed}: ${names}`, { autoClose: 10000 });
            } else {
                toast.success(`Payslip sent on WhatsApp to ${sent} staff member${sent === 1 ? '' : 's'}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.error || 'Failed to send payslips');
        }
        setSendingSlips(false);
    };

    const t = data?.totals;
    const dedEntry = dedDrawer && (data?.entries || []).find((e) => e.id === dedDrawer.entryId);
    const bonusEntry = bonusDrawer && (data?.entries || []).find((e) => e.id === bonusDrawer.entryId);
    const summaryEntry = summaryId && (data?.entries || []).find((e) => e.id === summaryId);

    return (
        <div className="sal">
            {pendingOps > 0 && <div className="sal__busyBar" />}
            <div className="sal__head">
                <div>
                    <h1 className="sal__title">{activeTab === 'attendance' ? 'Attendance & Leaves' : 'Salaries'}</h1>
                    {activeTab === 'salaries' && (
                        <p className="sal__sub">Monthly payroll — MOHRE is fixed; holiday, bonus, deductions and absence adjust the conditional bonus.</p>
                    )}
                </div>
                {activeTab === 'salaries' && (
                    <div className="sal__monthNav">
                        <button className="sal__navBtn" onClick={() => step(-1)}>‹</button>
                        <span className="sal__month">{MONTHS[month - 1]} {year}</span>
                        <button className="sal__navBtn" onClick={() => step(1)}>›</button>
                    </div>
                )}
            </div>

            {isManager && (
                <div className="sal__actionsRow">
                    <div className="sal__tabs">
                        <button className={activeTab === 'salaries' ? 'is-active' : ''} onClick={() => switchTab('salaries')}>Salaries</button>
                        <button className={activeTab === 'attendance' ? 'is-active' : ''} onClick={() => switchTab('attendance')}>Attendance &amp; Leaves</button>
                    </div>
                    {activeTab === 'salaries' && data?.run && (
                        <div className="sal__actionsRight">
                            {selCount > 0 && (
                                <button className="sal__teamBtn sal__waBtn" onClick={() => setSlipReview(true)} disabled={sendingSlips}>
                                    {sendingSlips ? 'Sending…' : `📲 WhatsApp payslips (${selCount})…`}
                                </button>
                            )}
                            <button className="sal__teamBtn" onClick={() => setBulk({ mode: 'deduct' })}>− Deduct from {selCount ? `selected (${selCount})` : 'everyone'}</button>
                            <button className="sal__teamBtn sal__teamBtn--pos" onClick={() => setBulk({ mode: 'bonus' })}>+ Bonus to {selCount ? `selected (${selCount})` : 'everyone'}</button>
                            <button className="sal__exportBtn" onClick={exportCsv}>⭳ Export CSV</button>
                            <input
                                ref={sifInputRef}
                                type="file"
                                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                style={{ display: 'none' }}
                                onChange={(e) => uploadSif(e.target.files?.[0])}
                            />
                            <button
                                className="sal__exportBtn sal__sifBtn"
                                onClick={() => sifInputRef.current && sifInputRef.current.click()}
                                disabled={sifBusy}
                                title="Upload the Al Ansari WPS SIF template — it comes back with this month's figures filled in"
                            >
                                {sifBusy ? '⏳ Filling…' : '⭱ Fill WPS SIF'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'attendance' ? <Attendance /> : (
            <>
            {loading ? (
                <div className="sal__empty">Loading…</div>
            ) : !data?.run ? (
                <div className="sal__openCard">
                    <div className="sal__openTitle">{MONTHS[month - 1]} {year} hasn’t been opened yet</div>
                    <div className="sal__openSub">{data?.eligible_count ?? 0} active staff will be added with their current salaries. You can then edit each row.</div>
                    <button className="sal__openBtn" onClick={openMonth} disabled={busy}>
                        {busy ? 'Opening…' : `Open ${MONTHS[month - 1]} ${year}`}
                    </button>
                </div>
            ) : (
                <>
                    <div className="sal__cards">
                        <SummaryCard label="Total payroll" value={moneyWhole(t.total)} />
                        <SummaryCard label="Paid" value={moneyWhole(t.paid)} tone="good" hint="Includes salary advances already handed out" />
                        <SummaryCard label="Remaining" value={moneyWhole(t.remaining)} tone={t.remaining > 0 ? 'warn' : 'good'} />
                        <SummaryCard label="WPS subtotal" value={moneyWhole(t.wps)} hint="Still to transfer for company-ID (WPS) staff — advances already paid are excluded" />
                    </div>

                    <div className="sal__tableWrap">
                        <table className="sal__table">
                            <thead>
                                <tr>
                                    <th className="sal__stickyCol">
                                        <div className="sal__nameHead">
                                            <input type="checkbox" className="sal__selAllBox" checked={allSelected} onChange={toggleAll} title="Select all" />
                                            <input type="search" className="sal__search" placeholder="Name" value={search} onChange={(e) => setSearch(e.target.value)} />
                                        </div>
                                    </th>
                                    <th>Breakdown</th>
                                    <th>Total</th>
                                    <th>Daily Rate</th>
                                    <th>Annual Leave</th>
                                    <th>Bonus</th>
                                    <th>Deductions</th>
                                    <th>Pro-Rata</th>
                                    <th>Payment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map(([role, rows]) => (
                                    <React.Fragment key={role}>
                                        <tr className="sal__roleRow"><td className="sal__roleLabel">{role}</td><td className="sal__roleFill" colSpan={8}></td></tr>
                                        {rows.map((e) => {
                                            const c = e.computed;
                                            return (
                                                <tr key={e.id} className={`sal__row${selected.has(e.id) ? ' is-selected' : ''}`} onClick={(ev) => openSummary(ev, e)}>
                                                    <td className="sal__stickyCol sal__name">
                                                        <input type="checkbox" className="sal__rowCheck" checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} />
                                                        <span className="sal__nameBlock">
                                                            <span className="sal__nameRow">
                                                                <button className="sal__nameText" title="Edit team member" onClick={() => setTeamEdit({ userId: e.user_id, entryId: e.id })}>{fullName(e)}</button>
                                                                <button className="sal__rmStaff" title="Remove from this month" onClick={() => removeStaff(e.id, fullName(e))}>✕</button>
                                                            </span>
                                                            {c.first_day && <span className="sal__workDay">First Working Day: {fmtDate(c.first_day)}</span>}
                                                            {c.last_day && <span className="sal__workDay sal__workDay--last">Last Working Day: {fmtDate(c.last_day)}</span>}
                                                        </span>
                                                    </td>
                                                    <td className="sal__breakdown" onClick={(ev) => ev.stopPropagation()}>
                                                        <div className="sal__bdGrid">
                                                            <div className="sal__bdField">
                                                                <span className="sal__bdLbl">MOHRE</span>
                                                                <EditCell value={e.mohre_salary} width={72} onCommit={(v) => saveField(e.id, 'mohre_salary', v)} />
                                                            </div>
                                                            <div className="sal__bdField">
                                                                <span className="sal__bdLbl">Conditional</span>
                                                                <EditCell value={e.conditional_bonus} width={72} onCommit={(v) => saveField(e.id, 'conditional_bonus', v)} />
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="sal__baseTotal" title="MOHRE + Conditional bonus">{moneyWhole(Number(e.mohre_salary || 0) + Number(e.conditional_bonus || 0))}</td>
                                                    <td className="sal__daily" title="(MOHRE + Conditional) ÷ days in month">{money(c.daily_rate)}</td>
                                                    <td className="sal__leaveCell"><LeaveCell e={e} /></td>
                                                    <td className="sal__addCol">
                                                        {c.additions_total > 0
                                                            ? <button className="sal__addLink" title="Manage bonus & holiday" onClick={() => setBonusDrawer({ entryId: e.id, name: fullName(e) })}>+{whole(c.additions_total)}</button>
                                                            : <button className="sal__dedAddBtn sal__dedAddBtn--bonus" title="Add bonus or holiday" onClick={() => setBonusDrawer({ entryId: e.id, name: fullName(e) })}>+</button>}
                                                        {e.suggestions?.length > 0 && (
                                                            <button className="sal__suggestBtn" title={`${e.suggestions.length} suggested pay item${e.suggestions.length === 1 ? '' : 's'} — click to review`} onClick={() => setBonusDrawer({ entryId: e.id, name: fullName(e) })}>💡</button>
                                                        )}
                                                    </td>
                                                    <td className="sal__dedCol">
                                                        {c.total_deductions > 0
                                                            ? <button className="sal__dedLink" title="Manage deductions" onClick={() => setDedDrawer({ entryId: e.id, name: fullName(e) })}>−{whole(c.total_deductions)}</button>
                                                            : <button className="sal__dedAddBtn sal__dedAddBtn--ded" title="Add deduction" onClick={() => setDedDrawer({ entryId: e.id, name: fullName(e) })}>−</button>}
                                                    </td>
                                                    <td className="sal__total" title={`Conditional after adjustments: ${money(c.conditional_effective)}${c.paid_days < data.run.days_in_month ? ` · ${clean(c.paid_days)}/${data.run.days_in_month} days` : ''}`}>{moneyWhole(c.total)}</td>
                                                    <td className="sal__payCol">
                                                        {e.paid_status === 'paid' ? (
                                                            <button className="sal__payBtn sal__payBtn--paid" onClick={() => pay(e.id, { unpay: true })} title="Fully paid · click to undo">✓ {whole(c.paid_total)} paid</button>
                                                        ) : (
                                                            <button className="sal__payBtn sal__payBtn--pending" onClick={() => pay(e.id, { full: true })} title={`Mark ${money(c.net_payable)} paid`}>{whole(c.remaining)} Pending</button>
                                                        )}
                                                        {c.advance > 0 && <div className="sal__advNote" title="Salary advance already paid — counted in Paid, not Remaining">incl. {moneyWhole(c.advance)} advance</div>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="sal__stickyCol">Totals ({data.entries.length})</td>
                                    <td colSpan={6}></td>
                                    <td className="sal__total">{moneyWhole(t.total)}</td>
                                    <td className="sal__payCol"><span className="sal__remaining">{moneyWhole(t.paid)} paid</span></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {data.removed?.length > 0 && (
                        <div className="sal__removed">
                            <div className="sal__removedTitle">Removed from {MONTHS[month - 1]} {year} · {data.removed.length}</div>
                            <div className="sal__removedList">
                                {data.removed.map((r) => (
                                    <div key={r.id} className="sal__removedItem">
                                        <span className="sal__removedName">{[r.first_name, r.last_name].filter(Boolean).join(' ')}</span>
                                        <span className="sal__removedRole">{r.role_snapshot}</span>
                                        <button className="sal__addBackBtn" onClick={() => restoreStaff(r.id)}>+ Add back</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
            </>
            )}

            {dedDrawer && (
                <DeductionsDrawer entry={dedEntry} name={dedDrawer.name} year={year} month={month}
                    onClose={() => setDedDrawer(null)} onAdd={addDeduction} onRemove={removeDeduction}
                    onEdit={editDeduction} onChanged={fetchMonth} />
            )}
            {bonusDrawer && (
                <BonusDrawer entry={bonusEntry} name={bonusDrawer.name}
                    onClose={() => setBonusDrawer(null)} onAdd={addAddition} onRemove={removeAddition}
                    onEdit={editAddition} />
            )}
            {bulk && (
                <BulkDrawer mode={bulk.mode} targetLabel={targetLabel}
                    targets={selected.size ? (data?.entries || []).filter((e) => selected.has(e.id)) : (data?.entries || [])}
                    onClose={() => setBulk(null)} onApply={(body) => applyBulk(bulk.mode, body)} onRemove={(ids) => bulkDelete(bulk.mode, ids)} />
            )}
            {summaryEntry && (
                <SummaryModal entry={summaryEntry} monthLabel={`${MONTHS[month - 1]} ${year}`} run={data.run}
                    onClose={() => setSummaryId(null)}
                    onEdit={() => { setTeamEdit({ userId: summaryEntry.user_id, entryId: summaryEntry.id }); setSummaryId(null); }} />
            )}

            {teamEdit && (
                <div className="team-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setTeamEdit(null); }}>
                    <div className="team-modal">
                        <div className="team-modal-header">
                            <h3>Edit Team Member</h3>
                            <button type="button" className="team-modal-close" onClick={() => setTeamEdit(null)} title="Close">×</button>
                        </div>
                        <div className="team-modal-body">
                            <EditTeamMember memberId={teamEdit.userId} payroll={{ entryId: teamEdit.entryId, monthLabel: `${MONTHS[month - 1]} ${year}` }}
                                onClose={() => setTeamEdit(null)} onSaved={() => { setTeamEdit(null); fetchMonth(); }} />
                        </div>
                    </div>
                </div>
            )}
            {sifResult && (
                <SifResultModal result={sifResult} monthLabel={`${MONTHS[month - 1]} ${year}`} busy={sifBusy}
                    onClose={() => setSifResult(null)}
                    onRefill={(rows) => uploadSif(sifFileRef.current, rows)}
                    onDownload={downloadSifAsIs} />
            )}
            {slipReview && (
                <PayslipSendReview
                    entries={(data?.entries || []).filter((e) => selected.has(e.id))}
                    monthLabel={`${MONTHS[month - 1]} ${year}`}
                    sending={sendingSlips}
                    onCancel={() => setSlipReview(false)}
                    onConfirm={sendPayslips} />
            )}
        </div>
    );
}

// Post-fill summary: what the WPS SIF fill wrote and anything the manager should
// double-check. When file rows have no payroll match, the download is HELD and
// this modal asks for their MOHRE + variance (or lets the manager download
// without them); the two figures drive the whole row, same as our own staff.
function SifResultModal({ result, monthLabel, onClose, onRefill, onDownload, busy }) {
    const s = result.summary || {};
    const notInPayroll = s.not_in_payroll || [];
    const notInFile = s.in_payroll_not_in_file || [];
    const noMol = s.in_payroll_no_mol || [];
    const filled = s.filled || [];
    const withJust = filled.filter((f) => f.justification);
    const pendingInput = !result.downloaded && notInPayroll.length > 0;
    // Manual figures per missing MOL; MOHRE prefills from the file's own contract.
    const [inputs, setInputs] = useState(() => Object.fromEntries(
        notInPayroll.map((p) => [p.mol, { mohre: p.contract ? String(p.contract) : '', variance: '' }])
    ));
    const setField = (mol, field, val) => setInputs((prev) => ({ ...prev, [mol]: { ...prev[mol], [field]: val } }));
    const submitManual = () => {
        const rows = notInPayroll
            .map((p) => ({ mol: p.mol, mohre: Number(inputs[p.mol]?.mohre || 0), variance: Number(inputs[p.mol]?.variance || 0) }))
            .filter((r) => r.mohre > 0);
        if (!rows.length) { toast.error('Enter a MOHRE salary for at least one employee'); return; }
        onRefill(rows);
    };
    return (
        <div className="team-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="team-modal sal__sifModal">
                <div className="team-modal-header">
                    <h3>{pendingInput ? 'WPS SIF · missing employees' : 'WPS SIF filled'} · {monthLabel}</h3>
                    <button type="button" className="team-modal-close" onClick={onClose} title="Close">×</button>
                </div>
                <div className="team-modal-body">
                    <div className="sal__sifDone">
                        ✅ <b>{filled.length}</b> of <b>{s.edr_rows}</b> rows filled ·
                        Total Salary <b>{moneyWhole(s.total_salary)}</b>
                        {filled.some((f) => f.manual) && <> · <b>{filled.filter((f) => f.manual).length}</b> entered manually</>}
                        {result.downloaded ? (
                            <div className="sal__sifDoneSub">Downloaded as <code>{result.filename}</code> — upload this file to Al Ansari.</div>
                        ) : (
                            <div className="sal__sifDoneSub">Not downloaded yet — fill in the missing employees below, or download without them.</div>
                        )}
                    </div>

                    {pendingInput && (
                        <div className="sal__sifWarnBlock sal__sifManual">
                            <div className="sal__sifWarnTitle">⚠️ In the SIF but not in this month’s payroll ({notInPayroll.length}) — enter their figures to fill them too</div>
                            {notInPayroll.map((p, i) => (
                                <div key={i} className="sal__sifManualRow">
                                    <span className="sal__sifManualName">{p.name || '(no name)'} <span className="sal__sifMol">MOL {p.mol || '—'}</span></span>
                                    <input className="sal__sifManualInput" type="number" placeholder="MOHRE (basic)"
                                        value={inputs[p.mol]?.mohre ?? ''} onChange={(e) => setField(p.mol, 'mohre', e.target.value)} />
                                    <input className="sal__sifManualInput" type="number" placeholder="Variance (+/−)"
                                        value={inputs[p.mol]?.variance ?? ''} onChange={(e) => setField(p.mol, 'variance', e.target.value)} />
                                </div>
                            ))}
                            <div className="sal__sifManualHint">MOHRE is prefilled from the file’s own contract column. Variance = what’s paid on top of (+) or below (−) it — the justification (Bonus/Deduction) writes itself. Rows left without a MOHRE stay untouched.</div>
                            <div className="sal__sifManualActions">
                                <button className="sal__sifSecondary" onClick={onDownload} disabled={busy}>Download without them</button>
                                <button className="sal__sifPrimary" onClick={submitManual} disabled={busy}>{busy ? 'Filling…' : 'Fill & download'}</button>
                            </div>
                        </div>
                    )}

                    {(!pendingInput && (notInPayroll.length > 0 || notInFile.length > 0)) || (pendingInput && notInFile.length > 0) ? (
                        <div className="sal__sifWarn">
                            {!pendingInput && notInPayroll.length > 0 && (
                                <div className="sal__sifWarnBlock">
                                    <div className="sal__sifWarnTitle">⚠️ In the SIF but not in this month’s payroll ({notInPayroll.length}) — left unchanged</div>
                                    <ul>{notInPayroll.map((p, i) => <li key={i}>{p.name || '(no name)'} <span className="sal__sifMol">MOL {p.mol || '—'}</span></li>)}</ul>
                                </div>
                            )}
                            {notInFile.length > 0 && (
                                <div className="sal__sifWarnBlock">
                                    <div className="sal__sifWarnTitle">⚠️ In payroll (has MOL) but not in the SIF ({notInFile.length}) — not written anywhere</div>
                                    <ul>{notInFile.map((p, i) => <li key={i}>{p.name} <span className="sal__sifMol">MOL {p.mol}</span></li>)}</ul>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {withJust.length > 0 && (
                        <div className="sal__sifJust">
                            <div className="sal__sifJustTitle">Variance justifications written ({withJust.length})</div>
                            <table className="sal__sifTable">
                                <thead><tr><th>Employee</th><th>Variance</th><th>Reason</th></tr></thead>
                                <tbody>
                                    {withJust.map((f, i) => (
                                        <tr key={i}>
                                            <td>{f.name}{f.manual && <span className="sal__sifMol"> · manual</span>}</td>
                                            <td className={f.salary_variance >= 0 ? 'is-pos' : 'is-neg'}>{f.salary_variance >= 0 ? '+' : ''}{whole(f.salary_variance)}</td>
                                            <td>{f.justification}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {noMol.length > 0 && (
                        <div className="sal__sifNote">
                            {noMol.length} staff paid outside WPS (no MOL number) aren’t part of the SIF: {noMol.map((p) => p.name).join(', ')}.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Row 1 = amount-based (AED / % / Daily rate). Row 2 = day-count Unpaid Leave
// (no reason) + Advance. 'unpaid_leave' is a UI type that saves as a 'day' item.
/* Inline row editor for a bonus or deduction line.
 *
 * Deliberately edits in PLACE rather than opening a second drawer on top of the
 * first: the whole point is to correct a number you can already see, and a
 * stacked overlay would hide the list you are correcting it against.
 *
 * `options` is the type/kind selector for whichever list it sits in, so the
 * editor does not need to know whether it is editing an addition or a deduction.
 */
function InlineItemEdit({ item, options, optionKey, onSave, onCancel }) {
    const [opt, setOpt] = useState(item[optionKey] || options[0].key);
    const [amount, setAmount] = useState(String(item.amount ?? ''));
    const [reason, setReason] = useState(item.reason || '');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const n = Number(amount);
        // Mirrors the server: a non-positive amount is refused rather than
        // silently zeroing a line on someone's payslip.
        if (!(n > 0)) { toast.error('Amount must be positive'); return; }
        setSaving(true);
        try { await onSave({ [optionKey]: opt, amount: n, reason: reason.trim() || null }); }
        catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
        finally { setSaving(false); }
    };

    return (
        <div className="sal__dedItem sal__dedItem--editing">
            <div className="sal__editRow">
                <div className="sal__editTypes">
                    {options.map((o) => (
                        <button key={o.key} type="button"
                            className={`sal__dedType${opt === o.key ? ' is-active' : ''}`}
                            onClick={() => setOpt(o.key)}>{o.label}</button>
                    ))}
                </div>
                <div className="sal__editFields">
                    <input className="sal__editAmount" type="number" step="0.01" min="0"
                        value={amount} onChange={(e) => setAmount(e.target.value)}
                        onFocus={(e) => e.target.select()} aria-label="Amount" />
                    <input className="sal__editReason" type="text" placeholder="Reason"
                        value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
                </div>
                {/* Save and Cancel get their own row. In the drawer's width the
                    four controls could not sit on one line, and Cancel wrapped
                    alone underneath a stranded Save (2026-08-31). */}
                <div className="sal__editActions">
                    <button className="sal__editCancel" onClick={onCancel} disabled={saving}>Cancel</button>
                    <button className="sal__editSave" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
}

const DED_TYPES = [
    { key: 'fixed', label: 'AED', place: 'Amount (AED)', row: 1, step: '0.01' },
    { key: 'percent', label: '%', place: 'Percent of full pay', row: 1, step: '0.01' },
    { key: 'day', label: 'Daily rate', place: 'Days × daily rate (e.g. 1.2)', row: 1, step: '0.1' },
    { key: 'unpaid_leave', label: 'Unpaid Leave', place: 'Number of days', row: 2, step: '1', noReason: true },
    { key: 'advance', label: 'Advance', place: 'Advance amount (AED)', row: 2, step: '0.01' },
];
const dedItemAmt = (i) => i.type === 'percent' ? `${clean(i.amount)}%` : i.type === 'day' ? dayLabel(i.amount) : moneyWhole(i.amount);

// Deductions slide-over: one-off items (AED / % / daily-rate), advances, recurring.
function DeductionsDrawer({ entry, name, year, month, onClose, onAdd, onRemove, onEdit, onChanged }) {
    const [type, setType] = useState('fixed');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [dates, setDates] = useState(''); // free-typed dates for Unpaid Leave
    const [saving, setSaving] = useState(false);

    const [recur, setRecur] = useState(null);
    const [rReason, setRReason] = useState('');
    const [rTotal, setRTotal] = useState('');
    const [rMode, setRMode] = useState('months');
    const [rVal, setRVal] = useState('');
    const [rSaving, setRSaving] = useState(false);

    const userId = entry?.user_id;
    const loadRecur = useCallback(async () => {
        if (!userId) return;
        try { setRecur((await axios.get(`/api/salaries/recurring/${userId}`)).data); } catch { setRecur([]); }
    }, [userId]);
    useEffect(() => { loadRecur(); }, [loadRecur]);

    const items = entry?.deductions || [];
    const deductions = items.filter((i) => i.type !== 'advance');
    const advances = items.filter((i) => i.type === 'advance');
    const c = entry?.computed;
    const dt = DED_TYPES.find((tp) => tp.key === type) || DED_TYPES[0];

    const submit = async () => {
        if (!(Number(amount) > 0)) { toast.error('Enter an amount'); return; }
        setSaving(true);
        try {
            // Unpaid Leave is stored as a day-rate deduction; the typed dates become
            // the reason so the payslip shows which days were unpaid.
            const body = type === 'unpaid_leave'
                ? { type: 'day', amount: Number(amount), reason: `Unpaid leave${dates.trim() ? `: ${dates.trim()}` : ''}` }
                : { type, amount: Number(amount), reason: reason.trim() || null };
            await onAdd(entry.id, body);
            setAmount(''); setReason(''); setDates('');
            toast.success(type === 'advance' ? 'Advance recorded' : 'Deduction added');
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to add'); }
        finally { setSaving(false); }
    };
    const remove = async (id) => { try { await onRemove(id); } catch { toast.error('Failed to remove'); } };
    const [editId, setEditId] = useState(null);

    const addRecur = async () => {
        if (!(Number(rTotal) > 0) || !(Number(rVal) > 0)) { toast.error('Enter a total and a value'); return; }
        setRSaving(true);
        try {
            await axios.post('/api/salaries/recurring', {
                user_id: userId, reason: rReason.trim() || null, total_amount: Number(rTotal),
                start_year: year, start_month: month, mode: rMode,
                months: rMode === 'months' ? Number(rVal) : undefined,
                monthly_amount: rMode === 'monthly' ? Number(rVal) : undefined,
            });
            setRReason(''); setRTotal(''); setRVal(''); await loadRecur(); onChanged(); toast.success('Recurring deduction added');
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to add'); }
        finally { setRSaving(false); }
    };
    const cancelRecur = async (id) => {
        if (!window.confirm('Cancel this recurring deduction? Future installments stop; past ones stay.')) return;
        try { await axios.post(`/api/salaries/recurring/${id}/cancel`); await loadRecur(); onChanged(); } catch { toast.error('Failed to cancel'); }
    };

    return (
        <div className="sal__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sal__drawer">
                <div className="sal__drawerHead">
                    <div>
                        <div className="sal__drawerTitle">Deductions &amp; advances</div>
                        <div className="sal__drawerSub">{name}{c ? ` · ${moneyWhole(c.total)} total` : ''}</div>
                    </div>
                    <button className="sal__drawerClose" onClick={onClose}>✕</button>
                </div>
                <div className="sal__drawerBody">
                    <div className="sal__dedForm">
                        <div className="sal__dedFormTitle">Add a deduction or advance</div>
                        <div className="sal__dedTypeRow">
                            {DED_TYPES.filter((tp) => tp.row === 1).map((tp) => (<button key={tp.key} className={`sal__dedType${type === tp.key ? ' is-active' : ''}`} onClick={() => setType(tp.key)}>{tp.label}</button>))}
                        </div>
                        <div className="sal__dedTypeRow">
                            {DED_TYPES.filter((tp) => tp.row === 2).map((tp) => (<button key={tp.key} className={`sal__dedType${type === tp.key ? ' is-active' : ''}`} onClick={() => setType(tp.key)}>{tp.label}</button>))}
                        </div>
                        {!dt.noReason && <input className="sal__dedInput" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />}
                        {type === 'unpaid_leave' && <input className="sal__dedInput" placeholder="Which dates? (e.g. 5, 12, 20)" value={dates} onChange={(e) => setDates(e.target.value)} />}
                        <input className="sal__dedInput" type="number" step={dt.step} placeholder={dt.place} value={amount} onChange={(e) => setAmount(e.target.value)} />
                        <div className="sal__dedHint">
                            {type === 'percent' ? 'Deducts this % of the full pay (MOHRE + conditional).'
                                : type === 'day' ? 'Deducts the daily rate × this number (1.2 = 120% of one day).'
                                    : type === 'unpaid_leave' ? 'Deducts the daily rate × this many unpaid days.'
                                        : type === 'advance' ? 'Salary paid early — counts as PAID (not a deduction) and lowers what’s remaining.'
                                            : 'A fixed AED amount deducted from this month’s pay.'}
                        </div>
                        <button className="sal__dedAdd" onClick={submit} disabled={saving || !entry}>{saving ? 'Saving…' : type === 'advance' ? 'Record advance' : 'Add deduction'}</button>
                    </div>

                    <div className="sal__dedSection">Deductions this month</div>
                    {deductions.length === 0 ? <div className="sal__drawerEmpty">No one-off deductions.</div> : deductions.map((i) => (
                        editId === i.id ? (
                            <InlineItemEdit key={i.id} item={i} optionKey="type"
                                options={DED_TYPES.filter((t) => ['fixed', 'percent', 'day'].includes(t.key))}
                                onCancel={() => setEditId(null)}
                                onSave={async (body) => { await onEdit(i.id, body); setEditId(null); toast.success('Deduction updated'); }} />
                        ) : (
                        <div key={i.id} className="sal__dedItem">
                            <div className="sal__dedItemMain">
                                <span className="sal__dedItemReason">{i.reason || (i.type === 'day' ? 'Daily-rate deduction' : i.type === 'percent' ? 'Percentage deduction' : 'Deduction')}</span>
                                <span className="sal__dedItemType">{i.type === 'day' ? 'daily rate' : i.type === 'percent' ? 'of full pay' : 'fixed AED'}</span>
                            </div>
                            <span className="sal__dedItemAmt sal__dedItemAmt--neg">−{dedItemAmt(i)}</span>
                            <span className="sal__dedItemActions">
                                <button className="sal__dedItemEdit" title="Edit" onClick={() => setEditId(i.id)}>✎</button>
                                <button className="sal__dedItemDel" title="Remove" onClick={() => remove(i.id)}>✕</button>
                            </span>
                        </div>
                        )
                    ))}

                    <div className="sal__dedSection">Salary advances</div>
                    {advances.length === 0 ? <div className="sal__drawerEmpty">No advances.</div> : advances.map((i) => (
                        <div key={i.id} className="sal__dedItem sal__dedItem--adv">
                            <div className="sal__dedItemMain">
                                <span className="sal__dedItemReason">{i.reason || 'Salary advance'}</span>
                                <span className="sal__dedItemType">paid early · counts as paid</span>
                            </div>
                            <span className="sal__dedItemAmt sal__dedItemAmt--adv">{moneyWhole(i.amount)}</span>
                            <button className="sal__dedItemDel" title="Remove" onClick={() => remove(i.id)}>✕</button>
                        </div>
                    ))}

                    <div className="sal__dedSection">Recurring deductions</div>
                    {recur === null ? <div className="sal__drawerEmpty">Loading…</div> : recur.length === 0 ? <div className="sal__drawerEmpty">No recurring deductions.</div> : recur.map((d) => (
                        <div key={d.id} className={`sal__ded${d.status !== 'active' ? ' sal__ded--done' : ''}`}>
                            <div className="sal__dedTop">
                                <span className="sal__dedReason">{d.reason || 'Deduction'}</span>
                                <span className={`sal-chip ${d.status === 'active' ? 'sal-chip--partial' : d.status === 'completed' ? 'sal-chip--paid' : 'sal-chip--unpaid'}`}>{d.status}</span>
                            </div>
                            <div className="sal__dedMeta">{moneyWhole(d.total_amount)} total · {moneyWhole(d.monthly_amount)}/mo · <strong>{moneyWhole(d.remaining_amount)} left</strong></div>
                            {d.installments?.length > 0 && (
                                <div className="sal__dedSched">{d.installments.map((i) => (<span key={i.id} className="sal__dedInst">{MONTHS[i.month - 1].slice(0, 3)} {String(i.year).slice(2)}: {moneyWhole(i.amount)}</span>))}</div>
                            )}
                            {d.status === 'active' && <button className="sal__dedCancel" onClick={() => cancelRecur(d.id)}>Cancel</button>}
                        </div>
                    ))}
                    <div className="sal__dedForm">
                        <div className="sal__dedFormTitle">Add recurring deduction</div>
                        <input className="sal__dedInput" placeholder="Reason" value={rReason} onChange={(e) => setRReason(e.target.value)} />
                        <input className="sal__dedInput" type="number" placeholder="Total amount (AED)" value={rTotal} onChange={(e) => setRTotal(e.target.value)} />
                        <div className="sal__dedModes">
                            <button className={`sal__dedMode${rMode === 'months' ? ' is-active' : ''}`} onClick={() => setRMode('months')}>Split over N months</button>
                            <button className={`sal__dedMode${rMode === 'monthly' ? ' is-active' : ''}`} onClick={() => setRMode('monthly')}>Fixed monthly amount</button>
                        </div>
                        <input className="sal__dedInput" type="number" placeholder={rMode === 'months' ? 'Number of months' : 'Amount per month (AED)'} value={rVal} onChange={(e) => setRVal(e.target.value)} />
                        <div className="sal__dedHint">Starts {MONTHS[month - 1]} {year}; auto-deducts each month until the balance is 0.</div>
                        <button className="sal__dedAdd" onClick={addRecur} disabled={rSaving}>{rSaving ? 'Adding…' : 'Add recurring'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const addItemAmt = (i) => i.amount_type === 'day' ? dayLabel(i.amount) : moneyWhole(i.amount);
const ADD_KINDS = [{ key: 'holiday', label: 'Holiday pay' }, { key: 'overtime', label: 'Overtime' }, { key: 'bonus', label: 'Bonus' }];

// Bonus slide-over: add a Holiday-pay / Overtime / Bonus line, as AED or daily-rate.
// "Pay now" marks it as an advance too — added to earnings AND counted as paid.
function BonusDrawer({ entry, name, onClose, onAdd, onRemove, onEdit }) {
    const [kind, setKind] = useState('bonus');
    const [amountType, setAmountType] = useState('fixed');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [payNow, setPayNow] = useState(false);
    const [saving, setSaving] = useState(false);

    const items = entry?.additions || [];
    const c = entry?.computed;

    const submit = async () => {
        if (!(Number(amount) > 0)) { toast.error('Enter an amount'); return; }
        setSaving(true);
        try {
            await onAdd(entry.id, { kind, amount_type: amountType, amount: Number(amount), reason: reason.trim() || null, is_advance: payNow });
            setAmount(''); setReason(''); setPayNow(false);
            toast.success(payNow ? 'Added & marked paid' : 'Added');
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to add'); }
        finally { setSaving(false); }
    };
    const remove = async (id) => { try { await onRemove(id); } catch { toast.error('Failed to remove'); } };
    const [editId, setEditId] = useState(null);

    // Pre-fill the add form from an attendance-derived suggestion (holiday pay or
    // overtime). Amount is a day-multiplier so it pays that many daily rates.
    const applySuggestion = (s) => {
        setKind(s.kind === 'overtime' ? 'overtime' : 'holiday');
        setAmountType('day');
        setAmount(String(s.days || 1));
        setReason(s.reason || '');
    };
    const suggestions = entry?.suggestions || [];
    const bankedDays = entry?.holiday_banked_days || 0;

    const section = (k, label) => {
        const list = items.filter((i) => i.kind === k);
        return (
            <>
                <div className="sal__dedSection">{label}</div>
                {list.length === 0 ? <div className="sal__drawerEmpty">None.</div> : list.map((i) => (
                    editId === i.id ? (
                        <InlineItemEdit key={i.id} item={i} optionKey="amount_type"
                            options={[{ key: 'fixed', label: 'AED' }, { key: 'day', label: 'Daily rate' }]}
                            onCancel={() => setEditId(null)}
                            onSave={async (body) => { await onEdit(i.id, { ...body, kind: i.kind }); setEditId(null); toast.success('Updated'); }} />
                    ) : (
                    <div key={i.id} className="sal__dedItem sal__dedItem--adv">
                        <div className="sal__dedItemMain">
                            <span className="sal__dedItemReason">{i.reason || label}</span>
                            <span className="sal__dedItemType">{i.amount_type === 'day' ? 'daily rate' : 'fixed AED'}{i.is_advance ? ' · paid now' : ''}</span>
                        </div>
                        {i.is_advance && <span className="sal__paidNowChip">paid</span>}
                        <span className="sal__dedItemAmt sal__dedItemAmt--adv">+{addItemAmt(i)}</span>
                        <span className="sal__dedItemActions">
                            <button className="sal__dedItemEdit" title="Edit" onClick={() => setEditId(i.id)}>✎</button>
                            <button className="sal__dedItemDel" title="Remove" onClick={() => remove(i.id)}>✕</button>
                        </span>
                    </div>
                    )
                ))}
            </>
        );
    };

    return (
        <div className="sal__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sal__drawer">
                <div className="sal__drawerHead">
                    <div>
                        <div className="sal__drawerTitle">Bonus, overtime &amp; holiday</div>
                        <div className="sal__drawerSub">{name}{c ? ` · +${moneyWhole(c.additions_total)} added` : ''}</div>
                    </div>
                    <button className="sal__drawerClose" onClick={onClose}>✕</button>
                </div>
                <div className="sal__drawerBody">
                    {bankedDays > 0 && (
                        <div className="sal__bankedBox">
                            <div className="sal__bankedMain">
                                <span className="sal__bankedLabel">UH · {whole(bankedDays)} unused holiday day{bankedDays === 1 ? '' : 's'} (banked as time off)</span>
                                <span className="sal__bankedSub">Paid day off, taken later from Attendance — set on the Attendance page.</span>
                            </div>
                        </div>
                    )}
                    {suggestions.length > 0 && (
                        <div className="sal__suggestBox">
                            <div className="sal__suggestTitle">💡 Suggested from attendance</div>
                            {suggestions.map((s, i) => (
                                <div key={i} className="sal__suggestRow">
                                    <div className="sal__suggestMain">
                                        <span className="sal__suggestLabel">{s.label}</span>
                                        <span className="sal__suggestReason">{s.reason}</span>
                                    </div>
                                    <button className="sal__suggestApply" onClick={() => applySuggestion(s)}>Add</button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="sal__dedForm">
                        <div className="sal__dedFormTitle">Add holiday pay, overtime or bonus</div>
                        <div className="sal__dedTypeRow">
                            {ADD_KINDS.map((k) => (<button key={k.key} className={`sal__dedType${kind === k.key ? ' is-active' : ''}`} onClick={() => setKind(k.key)}>{k.label}</button>))}
                        </div>
                        <div className="sal__dedTypeRow">
                            <button className={`sal__dedType${amountType === 'fixed' ? ' is-active' : ''}`} onClick={() => setAmountType('fixed')}>AED</button>
                            <button className={`sal__dedType${amountType === 'day' ? ' is-active' : ''}`} onClick={() => setAmountType('day')}>Daily rate</button>
                        </div>
                        <input className="sal__dedInput" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                        <input className="sal__dedInput" type="number" step={amountType === 'day' ? '0.1' : '0.01'} placeholder={amountType === 'day' ? 'Days × daily rate (e.g. 1.5)' : 'Amount (AED)'} value={amount} onChange={(e) => setAmount(e.target.value)} />
                        <label className="sal__payNow"><input type="checkbox" checked={payNow} onChange={(e) => setPayNow(e.target.checked)} /> Pay now (advance — counts as already paid)</label>
                        <div className="sal__dedHint">{amountType === 'day' ? 'Adds the daily rate × this number (1.5 = 150% of one day).' : 'Adds this fixed AED amount to the pay.'}</div>
                        <button className="sal__dedAdd" onClick={submit} disabled={saving || !entry}>{saving ? 'Saving…' : payNow ? 'Add & mark paid' : 'Add'}</button>
                    </div>

                    {section('holiday', 'Holiday pay')}
                    {section('overtime', 'Overtime')}
                    {section('bonus', 'Bonus')}
                </div>
            </div>
        </div>
    );
}

const BULK_DED_TYPES = [
    { key: 'percent', label: '%', place: 'Percent of each person’s pay' },
    { key: 'fixed', label: 'AED', place: 'Amount (AED) per person' },
    { key: 'day', label: 'Daily rate', place: 'Days × daily rate (e.g. 1)' },
];

// Slide-over to apply AND remove a shared deduction/bonus across a group
// (selected people, else everyone). Groups identical line items so a shared
// adjustment can be removed from the whole group in one click.
function BulkDrawer({ mode, targets, targetLabel, onClose, onApply, onRemove }) {
    const isBonus = mode === 'bonus';
    const [type, setType] = useState(isBonus ? 'fixed' : 'percent'); // deduct type OR bonus amount_type
    const [kind, setKind] = useState('bonus');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [payNow, setPayNow] = useState(false);
    const [saving, setSaving] = useState(false);
    const [removingKey, setRemovingKey] = useState(null);

    const apply = async () => {
        if (!(Number(amount) > 0)) { toast.error('Enter an amount'); return; }
        setSaving(true);
        try {
            const body = isBonus
                ? { kind, amount_type: type === 'day' ? 'day' : 'fixed', amount: Number(amount), reason: reason.trim() || null, is_advance: payNow }
                : { type, amount: Number(amount), reason: reason.trim() || null };
            const res = await onApply(body);
            toast.success(`Applied to ${res.applied ?? 0} people`);
            setAmount(''); setReason(''); setPayNow(false);
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to apply'); }
        finally { setSaving(false); }
    };

    // Group the target group's items by identical signature → the shared ones.
    const groups = useMemo(() => {
        const map = new Map();
        (targets || []).forEach((t) => {
            const items = isBonus ? (t.additions || []) : (t.deductions || []).filter((i) => i.type !== 'advance');
            items.forEach((i) => {
                const key = isBonus
                    ? `${i.kind}|${i.amount_type}|${Number(i.amount)}|${i.reason || ''}|${i.is_advance ? 1 : 0}`
                    : `${i.type}|${Number(i.amount)}|${i.reason || ''}`;
                if (!map.has(key)) map.set(key, { key, sample: i, ids: [], people: new Set() });
                const g = map.get(key); g.ids.push(i.id); g.people.add(t.id);
            });
        });
        return [...map.values()].sort((a, b) => b.people.size - a.people.size);
    }, [targets, isBonus]);

    const removeGroup = async (g) => {
        setRemovingKey(g.key);
        try { await onRemove(g.ids); toast.success(`Removed from ${g.people.size} ${g.people.size === 1 ? 'person' : 'people'}`); }
        catch (e) { toast.error('Failed to remove'); }
        finally { setRemovingKey(null); }
    };

    const groupLabel = (g) => {
        const i = g.sample;
        if (isBonus) return `+${addItemAmt(i)} · ${i.reason || i.kind}${i.is_advance ? ' · paid' : ''}`;
        return `−${dedItemAmt(i)} · ${i.reason || (i.type === 'day' ? 'Daily-rate' : i.type === 'percent' ? 'Percentage' : 'Deduction')}`;
    };

    const types = isBonus ? [{ key: 'fixed', label: 'AED', place: 'Amount (AED) per person' }, { key: 'day', label: 'Daily rate', place: 'Days × daily rate (e.g. 1.5)' }] : BULK_DED_TYPES;
    const place = types.find((tp) => tp.key === type)?.place || 'Amount';

    return (
        <div className="sal__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sal__drawer">
                <div className="sal__drawerHead">
                    <div>
                        <div className="sal__drawerTitle">{isBonus ? 'Bonus to team' : 'Deduct from team'}</div>
                        <div className="sal__drawerSub">Applies to {targetLabel} people</div>
                    </div>
                    <button className="sal__drawerClose" onClick={onClose}>✕</button>
                </div>
                <div className="sal__drawerBody">
                    <div className="sal__dedForm">
                        <div className="sal__dedFormTitle">{isBonus ? 'Add a shared bonus' : 'Add a shared deduction'}</div>
                        {isBonus && (
                            <div className="sal__dedTypeRow">
                                {ADD_KINDS.map((k) => (<button key={k.key} className={`sal__dedType${kind === k.key ? ' is-active' : ''}`} onClick={() => setKind(k.key)}>{k.label}</button>))}
                            </div>
                        )}
                        <div className="sal__dedTypeRow">
                            {types.map((tp) => (<button key={tp.key} className={`sal__dedType${type === tp.key ? ' is-active' : ''}`} onClick={() => setType(tp.key)}>{tp.label}</button>))}
                        </div>
                        <input className="sal__dedInput" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                        <input className="sal__dedInput" type="number" step={type === 'day' ? '0.1' : '0.01'} placeholder={place} value={amount} onChange={(e) => setAmount(e.target.value)} />
                        {isBonus && <label className="sal__payNow"><input type="checkbox" checked={payNow} onChange={(e) => setPayNow(e.target.checked)} /> Pay now (advance — counts as already paid)</label>}
                        <button className="sal__dedAdd" onClick={apply} disabled={saving}>{saving ? 'Applying…' : `Apply to ${targetLabel}`}</button>
                    </div>

                    <div className="sal__dedSection">Shared {isBonus ? 'bonuses' : 'deductions'} in this group</div>
                    {groups.length === 0 ? <div className="sal__drawerEmpty">None in the current selection.</div> : groups.map((g) => (
                        <div key={g.key} className={`sal__dedItem${isBonus ? ' sal__dedItem--adv' : ''}`}>
                            <div className="sal__dedItemMain">
                                <span className="sal__dedItemReason">{groupLabel(g)}</span>
                                <span className="sal__dedItemType">on {g.people.size} {g.people.size === 1 ? 'person' : 'people'}</span>
                            </div>
                            <button className="sal__dedItemDel" title="Remove from everyone in this group" disabled={removingKey === g.key} onClick={() => removeGroup(g)}>✕</button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// AED value of a deduction / addition item (percent + day need the base + rate).
const num = (v) => Number(v || 0);
const dedAED = (i, base, dailyRate) => i.type === 'percent' ? base * num(i.amount) / 100 : i.type === 'day' ? dailyRate * num(i.amount) : num(i.amount);
const addAED = (i, dailyRate) => i.amount_type === 'day' ? dailyRate * num(i.amount) : num(i.amount);
// Payment method (company-ID staff are on WPS). The payslip itself is rendered
// SERVER-side (routes/services/payslipPdf.js) — keep its payLines in sync.
const payMethod = (e) => (e.does_have_id_from_company === true ? 'WPS' : (e.salary_method || 'Cash'));
const cap = (s) => (s ? String(s).replace(/\b\w/g, (m) => m.toUpperCase()) : s);

// Build the earnings / deductions lines for the summary + payslip.
function payLines(entry) {
    const c = entry.computed;
    const base = num(entry.mohre_salary) + num(entry.conditional_bonus);
    const dr = num(c.daily_rate);
    const condMax = num(entry.conditional_bonus);
    // The conditional is a MAXIMUM allowance. One-off deductions (performance,
    // client complaints…) are presented as reasons the person fell short of the
    // maximum — not as payslip deductions. Unpaid leave and recurring
    // installments stay REAL deductions, so full allowance for good performance
    // can coexist with e.g. a loan installment shown separately.
    const isUnpaidLeaveItem = (i) => /^Unpaid leave \(attendance\)/i.test(i.reason || '');
    const dedLabel = (i) => `${i.reason || (i.type === 'day' ? 'Daily-rate deduction' : i.type === 'percent' ? 'Percentage deduction' : 'Deduction')}${i.type === 'percent' ? ` (${clean(i.amount)}%)` : i.type === 'day' ? ` (${clean(i.amount)} days)` : ''}`;
    const shortfalls = (entry.deductions || [])
        .filter((i) => i.type !== 'advance' && !isUnpaidLeaveItem(i))
        .map((i) => ({ label: dedLabel(i), amount: dedAED(i, base, dr), date: i.created_at }));
    const shortTotal = shortfalls.reduce((s, x) => s + x.amount, 0);
    const allowanceEarned = Math.max(0, condMax - shortTotal);
    const allowanceExcess = Math.max(0, shortTotal - condMax); // shortfalls past the allowance eat into basic

    const earnings = [
        { label: 'Basic Salary', amount: num(entry.mohre_salary) },
        {
            label: shortTotal > 0 ? `Conditional Allowance (maximum ${moneyWhole(condMax)})` : 'Conditional Allowance',
            amount: allowanceEarned,
        },
    ];
    (entry.additions || []).forEach((i) => earnings.push({
        label: `${i.kind === 'holiday' ? 'Holiday pay' : i.kind === 'overtime' ? 'Overtime' : 'Bonus'}${i.reason ? ` — ${i.reason}` : ''}${i.is_advance ? ' (paid)' : ''}`,
        amount: addAED(i, dr),
    }));

    const deductions = [];
    if (c.unpaid_days > 0) deductions.push({ label: `Unpaid days (${clean(c.unpaid_days)} × daily rate)`, amount: num(c.absence_amount) });
    (entry.deductions || []).filter((i) => i.type !== 'advance' && isUnpaidLeaveItem(i))
        .forEach((i) => deductions.push({ label: dedLabel(i), amount: dedAED(i, base, dr) }));
    (entry.recurring_items || []).forEach((r) => deductions.push({ label: `Recurring: ${r.reason || 'installment'}`, amount: num(r.amount) }));
    if (allowanceExcess > 0) deductions.push({ label: 'Deduction from Basic Salary (conditional allowance fully used)', amount: allowanceExcess });

    // "Already paid" = salary advances (deduction type) AND any bonus/overtime/
    // holiday that was marked "pay now" (is_advance addition) — each dated.
    const advances = [
        ...(entry.deductions || []).filter((i) => i.type === 'advance')
            .map((i) => ({ label: i.reason || 'Salary advance', amount: num(i.amount), date: i.created_at })),
        ...(entry.additions || []).filter((i) => i.is_advance).map((i) => ({
            label: `${i.kind === 'holiday' ? 'Holiday pay' : i.kind === 'overtime' ? 'Overtime' : 'Bonus'}${i.reason ? ` — ${i.reason}` : ''} (paid now)`,
            amount: addAED(i, dr), date: i.created_at,
        })),
    ];
    return { earnings, shortfalls, deductions, advances };
}

// Second step before payslips go out on WhatsApp: names every recipient with
// the number the PDF will reach, flags anyone with no number on file, and
// makes the manager press an explicit Send. Messages can't be unsent, so the
// toolbar button alone must never fire a send (2026-07-26).
function PayslipSendReview({ entries, monthLabel, sending, onCancel, onConfirm }) {
    const withPhone = entries.filter((e) => String(e.phone_number || '').replace(/\D/g, ''));
    const noPhone = entries.filter((e) => !String(e.phone_number || '').replace(/\D/g, ''));
    return (
        <div className="sal__overlay" onClick={(e) => { if (e.target === e.currentTarget && !sending) onCancel(); }}>
            <div className="sal__drawer sal__waReview">
                <div className="sal__drawerHead">
                    <div>
                        <div className="sal__drawerTitle">Send payslips on WhatsApp</div>
                        <div className="sal__drawerSub">{monthLabel} · {withPhone.length} recipient{withPhone.length === 1 ? '' : 's'}</div>
                    </div>
                    <button className="sal__drawerClose" onClick={onCancel} disabled={sending}>✕</button>
                </div>
                <div className="sal__drawerBody">
                    <div className="sal__waWarn">
                        Each person below receives their own payslip PDF as a WhatsApp message. <strong>This cannot be undone.</strong> Check the names and numbers before sending.
                    </div>
                    <div className="sal__dedSection">Will receive a payslip</div>
                    {withPhone.length === 0 ? (
                        <div className="sal__drawerEmpty">Nobody in this selection has a WhatsApp number on file.</div>
                    ) : (
                        <ul className="sal__waList">
                            {withPhone.map((e) => (
                                <li key={e.id} className="sal__waRow">
                                    <span className="sal__waName">{fullName(e)}<span className="sal__waRole">{cap(e.role_snapshot)}</span></span>
                                    <span className="sal__waPhone">{e.phone_number}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {noPhone.length > 0 && (
                        <>
                            <div className="sal__dedSection">Will be skipped — no WhatsApp number</div>
                            <ul className="sal__waList">
                                {noPhone.map((e) => (
                                    <li key={e.id} className="sal__waRow sal__waRow--skip">
                                        <span className="sal__waName">{fullName(e)}<span className="sal__waRole">{cap(e.role_snapshot)}</span></span>
                                        <span className="sal__waPhone">—</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
                <div className="sal__waActions">
                    <button className="sal__waCancel" onClick={onCancel} disabled={sending}>Cancel</button>
                    <button className="sal__waSend" onClick={onConfirm} disabled={sending || withPhone.length === 0}>
                        {sending ? 'Sending…' : `Send ${withPhone.length} payslip${withPhone.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Employee summary (opened by clicking a row) with a Generate Payslip action.
function SummaryModal({ entry, monthLabel, run, onClose, onEdit }) {
    const c = entry.computed;
    const { earnings, shortfalls, deductions, advances } = payLines(entry);
    // Everything on the payslip/summary shows whole AED (rounded up, like the
    // table). Gross = sum of the ROUNDED lines so the figures always add up, and
    // Net stays equal to the actual pay (matches the salary table's Pro-Rata).
    const roundAmt = (n) => Math.ceil(Number(n || 0));
    const grossR = earnings.reduce((s, l) => s + roundAmt(l.amount), 0);
    const netR = roundAmt(c.total);

    // Server-rendered payslip (single source of truth — the same builder feeds
    // the WhatsApp send). Fetched as a BLOB and shown in a normal new tab via
    // Chrome's own PDF viewer — a direct new-tab navigation to /api can't be
    // used because the CRA dev proxy refuses text/html-Accept navigations and
    // serves the React app instead of the PDF. The tab opens synchronously in
    // the click so popup blockers stay quiet; nothing is saved anywhere.
    const generatePayslip = async () => {
        const tab = window.open('', '_blank');
        try {
            const res = await axios.get(`/api/salaries/${run.year}/${run.month}/payslips/${entry.id}`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (tab) tab.location = url; else window.location.href = url;
        } catch (e) {
            if (tab) tab.close();
            toast.error('Failed to generate the payslip');
        }
    };

    const Row = ({ label, amount, neg, dim }) => (
        <div className={`sal__sumRow${dim ? ' sal__sumRow--dim' : ''}`}><span>{label}</span><span className={neg ? 'sal__sumNeg' : ''}>{neg ? '−' : ''}{moneyWhole(amount)}</span></div>
    );

    return (
        <div className="sal__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sal__summary">
                <div className="sal__drawerHead">
                    <div>
                        <div className="sal__drawerTitle">{fullName(entry)} <span className="sal__sumNo">#PS{entry.id}</span></div>
                        <div className="sal__drawerSub">{cap(entry.role_snapshot)} · {monthLabel}</div>
                    </div>
                    <button className="sal__drawerClose" onClick={onClose}>✕</button>
                </div>
                <div className="sal__sumBody">
                    <div className="sal__sumGrid">
                        <div className="sal__sumStat"><span>Paid days</span><strong>{clean(c.paid_days)} / {run.days_in_month}</strong></div>
                        <div className="sal__sumStat"><span>Daily rate</span><strong>{moneyWhole(c.daily_rate)}</strong></div>
                        <div className="sal__sumStat"><span>Paid through</span><strong>{payMethod(entry)}</strong></div>
                        <div className="sal__sumStat"><span>Leave this month</span><strong>{clean(num(entry.annual_leave_days_used))} day{num(entry.annual_leave_days_used) === 1 ? '' : 's'}</strong></div>
                        {entry.leave && (
                            <div className="sal__sumStat">
                                <span>Leave remaining</span>
                                <strong
                                    title={`${clean(entry.leave.accrued)} accrued − ${clean(entry.leave.used)} taken to end of this month`
                                        + (entry.leave.booked_ahead ? `\n${clean(entry.leave.booked_ahead)} more already booked in later months → ${clean(entry.leave.remaining)} still bookable` : '')}
                                >
                                    {clean(entry.leave.balance)}/{clean(entry.leave.accrued)} days
                                    {/* Booked-ahead leave is real but not yet spent, so it sits beside
                                        the balance instead of inside it — same rule as the Attendance
                                        tab's "Left" column, which is where these figures come from. */}
                                    {num(entry.leave.booked_ahead) > 0 && (
                                        <em className="sal__leaveAhead">+{clean(entry.leave.booked_ahead)} booked ahead</em>
                                    )}
                                </strong>
                            </div>
                        )}
                        {c.first_day && <div className="sal__sumStat"><span>First working day</span><strong>{fmtDate(c.first_day)}</strong></div>}
                        {c.last_day && <div className="sal__sumStat"><span>Last working day</span><strong>{fmtDate(c.last_day)}</strong></div>}
                        {entry.paid_at && <div className="sal__sumStat"><span>Paid on</span><strong>{fmtStamp(entry.paid_at)}</strong></div>}
                    </div>

                    <div className="sal__sumSection">Earnings</div>
                    {earnings.slice(0, 2).map((l, i) => <Row key={i} label={l.label} amount={l.amount} />)}
                    {/* Why the allowance is below its maximum — explanatory, not deductions. */}
                    {shortfalls.map((l, i) => <Row key={`sf${i}`} label={`↳ ${l.label}`} amount={l.amount} neg dim />)}
                    {earnings.slice(2).map((l, i) => <Row key={`a${i}`} label={l.label} amount={l.amount} />)}
                    <div className="sal__sumRow sal__sumRow--tot"><span>Gross</span><span>{moneyWhole(grossR)}</span></div>

                    {deductions.length > 0 && <>
                        <div className="sal__sumSection">Deductions</div>
                        {deductions.map((l, i) => <Row key={i} label={l.label} amount={l.amount} neg />)}
                    </>}

                    {advances.length > 0 && <>
                        <div className="sal__sumSection">Advances (already paid)</div>
                        {advances.map((l, i) => <Row key={i} label={`${l.label}${l.date ? ` · ${fmtStamp(l.date)}` : ''}`} amount={l.amount} />)}
                    </>}

                    <div className="sal__sumNet"><span>Net (Pro-Rata)</span><span>{moneyWhole(netR)}</span></div>
                    <div className="sal__sumPay">
                        <span>{entry.paid_status === 'paid' ? `Fully paid${entry.paid_at ? ` on ${fmtStamp(entry.paid_at)}` : ''}` : `${moneyWhole(c.remaining)} pending`}</span>
                    </div>
                </div>
                <div className="sal__sumFoot">
                    <button className="sal__sumEdit" onClick={onEdit}>Edit details</button>
                    {entry.does_have_id_from_company === true && (
                        <button className="sal__sumPayslip" onClick={generatePayslip}>⭳ Generate Payslip</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value, tone, hint }) {
    return (
        <div className={`sal__card${tone ? ` sal__card--${tone}` : ''}`} title={hint || ''}>
            <div className="sal__cardLabel">{label}</div>
            <div className="sal__cardValue">{value}</div>
        </div>
    );
}
